import React, { useRef, useState } from "react";
import { FileText, Upload, Eye, Trash2, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { formatDateTime } from "../lib/format";

// Dokumen pendukung verifikasi supplier. Kunci harus sama dengan DOKUMEN_JENIS di backend.
export const DOKUMEN_LIST = [
  { key: "nib", label: "Dokumen Ijin Usaha (NIB)", singkat: "NIB" },
  { key: "npwp", label: "NPWP", singkat: "NPWP" },
  { key: "lainnya", label: "Dokumen Pendukung Lainnya", singkat: "Lainnya" },
];

export const DOKUMEN_MAX_BYTES = 700 * 1024; // 700 KB, sama dengan batas di backend

/** Ambil dokumen sebagai blob lalu buka di tab baru. */
export async function bukaDokumen(supplierId, jenis) {
  const { data } = await api.get(`/suppliers/${supplierId}/dokumen/${jenis}`, { responseType: "blob" });
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url;
  a.target = "_blank";
  a.rel = "noreferrer";
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Beri jeda agar tab sempat memuat berkas sebelum URL dilepas.
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

const ukuranKb = (b) => `${Math.max(1, Math.round((b || 0) / 1024))} KB`;

/** Panel unggah dokumen — dipakai pada Profil Usaha supplier. */
export function DokumenUploader({ supplierId, dokumen, onChanged }) {
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const refs = useRef({});

  const pilih = async (e, jenis) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const namaPdf = file.name.toLowerCase().endsWith(".pdf");
    if (file.type !== "application/pdf" && !namaPdf) {
      setError("Dokumen harus berformat PDF.");
      return;
    }
    if (file.size > DOKUMEN_MAX_BYTES) {
      setError("Ukuran dokumen maksimal 700 KB.");
      return;
    }
    setError("");
    setBusy(jenis);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(`/suppliers/${supplierId}/dokumen/${jenis}`, form);
      await onChanged();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(""); }
  };

  const hapus = async (jenis, label) => {
    if (!window.confirm(`Hapus ${label}?`)) return;
    setBusy(jenis);
    try {
      await api.delete(`/suppliers/${supplierId}/dokumen/${jenis}`);
      await onChanged();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(""); }
  };

  const lihat = async (jenis) => {
    try { await bukaDokumen(supplierId, jenis); }
    catch { setError("Dokumen gagal dibuka."); }
  };

  return (
    <div className="space-y-3" data-testid="dokumen-uploader">
      {DOKUMEN_LIST.map(({ key, label }) => {
        const meta = dokumen?.[key];
        return (
          <div key={key} className="border border-slate-200 rounded-md p-3 flex items-start gap-3">
            <div className={`w-9 h-9 rounded-md flex items-center justify-center shrink-0 ${
              meta ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"
            }`}>
              <FileText className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-slate-800">{label}</div>
              {meta ? (
                <div className="text-xs text-slate-500 mt-0.5 break-all">
                  {meta.nama_file} · {ukuranKb(meta.ukuran)} · {formatDateTime(meta.diunggah_pada)}
                </div>
              ) : (
                <div className="text-xs text-slate-400 mt-0.5">Belum diunggah</div>
              )}
              <div className="flex flex-wrap gap-2 mt-2">
                <input
                  ref={(el) => { refs.current[key] = el; }}
                  type="file"
                  accept="application/pdf,.pdf"
                  className="hidden"
                  onChange={(e) => pilih(e, key)}
                  data-testid={`input-dokumen-${key}`}
                />
                <button
                  type="button"
                  onClick={() => refs.current[key]?.click()}
                  disabled={busy === key}
                  className="kedan-btn-secondary !py-1 !px-2 text-xs"
                  data-testid={`unggah-dokumen-${key}`}
                >
                  <Upload className="w-3.5 h-3.5" /> {busy === key ? "Memproses..." : meta ? "Ganti" : "Unggah"}
                </button>
                {meta && (
                  <>
                    <button type="button" onClick={() => lihat(key)} className="kedan-btn-secondary !py-1 !px-2 text-xs" data-testid={`lihat-dokumen-${key}`}>
                      <Eye className="w-3.5 h-3.5" /> Lihat
                    </button>
                    <button type="button" onClick={() => hapus(key, label)} className="kedan-btn-secondary !py-1 !px-2 text-xs !text-red-600" data-testid={`hapus-dokumen-${key}`}>
                      <Trash2 className="w-3.5 h-3.5" /> Hapus
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })}
      <p className="text-xs text-slate-400">Format PDF, ukuran maksimal 700 KB per dokumen.</p>
      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md px-3 py-2" data-testid="dokumen-error">{error}</div>}
    </div>
  );
}

/** Ringkasan dokumen berbentuk chip — dipakai pada tabel verifikasi supplier. */
export function DokumenChips({ supplierId, dokumen }) {
  const [error, setError] = useState("");
  const buka = async (jenis) => {
    try { await bukaDokumen(supplierId, jenis); }
    catch { setError("Gagal membuka"); }
  };
  return (
    <div className="flex flex-wrap gap-1" data-testid={`dokumen-chips-${supplierId}`}>
      {DOKUMEN_LIST.map(({ key, singkat }) => {
        const ada = Boolean(dokumen?.[key]);
        return ada ? (
          <button
            key={key}
            type="button"
            onClick={() => buka(key)}
            title={`Lihat ${dokumen[key].nama_file}`}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
            data-testid={`chip-dokumen-${key}-${supplierId}`}
          >
            <CheckCircle2 className="w-3 h-3" /> {singkat}
          </button>
        ) : (
          <span
            key={key}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border bg-slate-50 text-slate-400 border-slate-200"
            title="Belum diunggah"
          >
            {singkat}
          </span>
        );
      })}
      {error && <span className="text-[11px] text-red-600">{error}</span>}
    </div>
  );
}
